/**
 * SMART PHOTO MATCHER
 * 
 * Intelligent photo matching that:
 * 1. Accepts photos without requiring folder structure
 * 2. Matches by name alone when possible (unique names)
 * 3. Flags conflicts for manual resolution (same name across brands)
 * 4. Provides manual search for unmatched photos
 * 
 * Usage: Upload photos with filenames like "Eclipse.jpg" or "ILNP - Eclipse.jpg"
 * The matcher will try to find the corresponding item automatically.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { normalizeTypography } from '../utils/normalizeTypography';
import {
  Upload, X, Check, ChevronDown, ChevronUp,
  Image, Loader2, CheckCircle2, AlertTriangle, HelpCircle
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import type { CollectionItem } from '../types';
import { ACCEPTED_IMAGE_TYPES, compressImage } from '../utils/imageUtils';
import { useStorageUsage } from '../hooks/useStorageUsage';
import StorageUpgradeModal from './StorageUpgradeModal';
import { HardDrive } from 'lucide-react';

interface PhotoToMatch {
  file: File;
  preview: string;
  filename: string;
  parsedName: string;
  parsedBrand?: string;
  status: 'pending' | 'matched' | 'conflict' | 'no-match' | 'uploading' | 'done' | 'error';
  matches: CollectionItem[];
  selectedMatch?: CollectionItem;
  error?: string;
}

interface SmartPhotoMatcherProps {
  items: CollectionItem[];
  userId: string;
  onClose: () => void;
  onComplete: () => void;
}

// Parse filename to extract potential brand and name. Values are passed
// through normalizeTypography so parsed candidates match against items
// that were saved with plain ASCII (or normalized by the backfill). If
// the OS or file explorer inserted a smart quote into the filename, we
// still match "Lil’ Sweet.jpg" against a "Lil' Sweet" item.
function parseFilename(filename: string): { name: string; brand?: string } {
  // Remove extension
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');

  // Common separators for "Brand - Name" format
  const separators = [' - ', ' – ', ' — ', '_-_', ' by '];

  for (const sep of separators) {
    if (nameWithoutExt.includes(sep)) {
      const [brand, ...rest] = nameWithoutExt.split(sep);
      return {
        brand: normalizeTypography(brand.trim()),
        name: normalizeTypography(rest.join(sep).trim()),
      };
    }
  }

  // No separator found - treat entire filename as name
  return { name: normalizeTypography(nameWithoutExt.trim()) };
}

// Normalize string for matching (lowercase, remove special chars)
function normalizeForMatch(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Find matching items by name (and optionally brand)
function findMatches(
  parsedName: string,
  parsedBrand: string | undefined,
  items: CollectionItem[]
): CollectionItem[] {
  const normalizedName = normalizeForMatch(parsedName);
  
  // If brand was parsed from filename, try exact brand+name match first
  if (parsedBrand) {
    const normalizedBrand = normalizeForMatch(parsedBrand);
    const exactMatches = items.filter(item => 
      normalizeForMatch(item.name) === normalizedName &&
      normalizeForMatch(item.brand) === normalizedBrand
    );
    if (exactMatches.length > 0) return exactMatches;
  }
  
  // Find all items with matching name (any brand)
  return items.filter(item => 
    normalizeForMatch(item.name) === normalizedName ||
    normalizeForMatch(item.name).includes(normalizedName) ||
    normalizedName.includes(normalizeForMatch(item.name))
  );
}

export default function SmartPhotoMatcher({ 
  items, 
  userId, 
  onClose, 
  onComplete 
}: SmartPhotoMatcherProps) {
  const [photos, setPhotos] = useState<PhotoToMatch[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPhoto, setExpandedPhoto] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  
  // Storage tracking
  const { usedBytes, limitBytes, nextTier } = useStorageUsage();
  const [photosSkippedStorage, setPhotosSkippedStorage] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Process files when added
  const processFiles = useCallback((files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );
    
    const newPhotos: PhotoToMatch[] = imageFiles.map(file => {
      const { name: parsedName, brand: parsedBrand } = parseFilename(file.name);
      const matches = findMatches(parsedName, parsedBrand, items);
      
      let status: PhotoToMatch['status'] = 'pending';
      let selectedMatch: CollectionItem | undefined;
      
      if (matches.length === 1) {
        // Unique match - auto-select
        status = 'matched';
        selectedMatch = matches[0];
      } else if (matches.length > 1) {
        // Multiple matches - needs manual selection
        status = 'conflict';
      } else {
        // No matches found
        status = 'no-match';
      }
      
      return {
        file,
        preview: URL.createObjectURL(file),
        filename: file.name,
        parsedName,
        parsedBrand,
        status,
        matches,
        selectedMatch
      };
    });
    
    setPhotos(prev => [...prev, ...newPhotos]);
  }, [items]);

  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  // Manual match selection
  const handleSelectMatch = (photoFilename: string, item: CollectionItem) => {
    setPhotos(prev => prev.map(photo => 
      photo.filename === photoFilename
        ? { ...photo, selectedMatch: item, status: 'matched' }
        : photo
    ));
  };

  // Manual search and match
  const handleManualMatch = (photoFilename: string, item: CollectionItem) => {
    setPhotos(prev => prev.map(photo => 
      photo.filename === photoFilename
        ? { ...photo, selectedMatch: item, status: 'matched', matches: [item] }
        : photo
    ));
    setExpandedPhoto(null);
  };

  // Remove photo from list
  const handleRemovePhoto = (filename: string) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.filename === filename);
      if (photo) URL.revokeObjectURL(photo.preview);
      return prev.filter(p => p.filename !== filename);
    });
  };

  // Upload all matched photos
  const handleUploadAll = async () => {
    const photosToUpload = photos.filter(p => p.status === 'matched' && p.selectedMatch);
    if (photosToUpload.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress({ done: 0, total: photosToUpload.length });
    let sessionBytes = 0;
    let storageSkipped = 0;
    
    for (let i = 0; i < photosToUpload.length; i++) {
      const photo = photosToUpload[i];
      
      // Update status to uploading
      setPhotos(prev => prev.map(p => 
        p.filename === photo.filename ? { ...p, status: 'uploading' } : p
      ));
      
      try {
        // Compress the image first (was missing — raw uploads waste storage!)
        const compressedFile = await compressImage(photo.file, 1200, 0.8);
        
        // Check storage AFTER compression (actual size that will be stored)
        if (usedBytes + sessionBytes + compressedFile.size > limitBytes) {
          storageSkipped++;
          setPhotosSkippedStorage(storageSkipped);
          setPhotos(prev => prev.map(p => 
            p.filename === photo.filename 
              ? { ...p, status: 'error', error: 'Storage full' } 
              : p
          ));
          setUploadProgress({ done: i + 1, total: photosToUpload.length });
          continue;
        }
        
        // Upload to Firebase Storage
        const timestamp = Date.now();
        const safeFilename = compressedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `users/${userId}/items/${photo.selectedMatch!.id}/${timestamp}_${safeFilename}`;
        const storageRef = ref(storage, storagePath);
        
        await uploadBytes(storageRef, compressedFile, {
          contentType: 'image/jpeg',
          cacheControl: 'public, max-age=31536000, immutable',
        });
        const downloadUrl = await getDownloadURL(storageRef);
        
        // Track bytes uploaded this session
        sessionBytes += compressedFile.size;
        
        // Update Firestore document
        const itemRef = doc(db, 'users', userId, 'items', photo.selectedMatch!.id);
        await updateDoc(itemRef, {
          photos: arrayUnion({
            url: downloadUrl,
            type: 'swatch',
            uploadedAt: new Date().toISOString(),
            source: 'smart_matcher'
          })
        });
        
        // Update status to done
        setPhotos(prev => prev.map(p => 
          p.filename === photo.filename ? { ...p, status: 'done' } : p
        ));
      } catch (error) {
        console.error('Error uploading photo:', error);
        setPhotos(prev => prev.map(p => 
          p.filename === photo.filename 
            ? { ...p, status: 'error', error: 'Upload failed' } 
            : p
        ));
      }
      
      setUploadProgress({ done: i + 1, total: photosToUpload.length });
    }

    setIsUploading(false);
    onComplete();
  };

  // Filter items for manual search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items.slice(0, 50);
    const query = normalizeForMatch(searchQuery);
    return items.filter(item => 
      normalizeForMatch(item.name).includes(query) ||
      normalizeForMatch(item.brand).includes(query)
    ).slice(0, 50);
  }, [items, searchQuery]);

  // Stats
  const stats = useMemo(() => ({
    total: photos.length,
    matched: photos.filter(p => p.status === 'matched').length,
    conflicts: photos.filter(p => p.status === 'conflict').length,
    noMatch: photos.filter(p => p.status === 'no-match').length,
    done: photos.filter(p => p.status === 'done').length,
    readyToUpload: photos.filter(p => p.status === 'matched' && p.selectedMatch).length
  }), [photos]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-modal w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-600 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-glass font-heading">
              📸 Smart Photo Matcher
            </h2>
            <p className="text-sm text-glass-muted font-body">
              Upload photos and we'll match them to your items by filename
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700/50 rounded-full transition-default"
          >
            <X className="icon-md text-glass-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-6 sm:p-8 text-center transition-all ${
              isDragging
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-slate-600 hover:border-slate-500'
            }`}
          >
            <Upload className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 ${isDragging ? 'text-status-accent' : 'text-glass-muted'}`} />
            <p className="text-glass-secondary font-body mb-2 text-sm sm:text-base">
              Drag & drop photos here, or tap to browse
            </p>
            <p className="text-xs text-glass-muted font-body mb-4">
              Tip: Name files like "Polish Name.jpg" or "Brand - Polish Name.jpg"
            </p>
            <label className="inline-block px-4 py-2.5 sm:py-2 bg-slate-700 text-white rounded-lg font-semibold cursor-pointer hover:bg-slate-600 active:bg-slate-500 transition-default font-body">
              Choose Files
              <input
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                multiple
                onChange={handleFileInput}
                className="hidden"
              />
            </label>
          </div>

          {/* Stats bar */}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 sm:gap-3 p-2.5 sm:p-3 bg-slate-800/60 rounded-lg">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Image className="w-4 h-4 text-glass-muted" />
                <span className="text-xs sm:text-sm text-glass font-body">{stats.total} photos</span>
              </div>
              {stats.matched > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-xs sm:text-sm text-status-success font-body">{stats.matched} matched</span>
                </div>
              )}
              {stats.conflicts > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-xs sm:text-sm text-status-warning font-body">{stats.conflicts} need selection</span>
                </div>
              )}
              {stats.noMatch > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <HelpCircle className="w-4 h-4 text-red-500" />
                  <span className="text-xs sm:text-sm text-status-error font-body">{stats.noMatch} no match</span>
                </div>
              )}
              {stats.done > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Check className="w-4 h-4 text-blue-500" />
                  <span className="text-xs sm:text-sm text-status-info font-body">{stats.done} uploaded</span>
                </div>
              )}
            </div>
          )}

          {/* Photo list */}
          <div className="space-y-2">
            {photos.map(photo => (
              <div
                key={photo.filename}
                className={`p-3 rounded-lg border ${
                  photo.status === 'done' ? 'bg-green-900/20 border-green-700' :
                  photo.status === 'matched' ? 'bg-slate-800/60 border-green-600' :
                  photo.status === 'conflict' ? 'bg-slate-800/60 border-amber-600' :
                  photo.status === 'no-match' ? 'bg-slate-800/60 border-red-600' :
                  photo.status === 'uploading' ? 'bg-slate-800/60 border-blue-600' :
                  photo.status === 'error' ? 'bg-red-900/20 border-red-700' :
                  'bg-slate-800/60 border-slate-600'
                }`}
              >
                {/* Top row: Thumbnail + filename + remove button */}
                <div className="flex items-start gap-3">
                  {/* Thumbnail */}
                  <img
                    src={photo.preview}
                    alt={photo.filename}
                    className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg flex-shrink-0"
                  />
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-glass truncate font-body text-sm sm:text-base">
                      {photo.filename}
                    </p>
                    <p className="text-xs text-glass-muted font-body">
                      Parsed: {photo.parsedBrand ? `${photo.parsedBrand} → ` : ''}{photo.parsedName}
                    </p>
                    
                    {/* Status-specific content */}
                    {photo.status === 'matched' && photo.selectedMatch && (
                      <p className="text-sm text-status-success font-body mt-1">
                        ✓ {photo.selectedMatch.brand} - {photo.selectedMatch.name}
                      </p>
                    )}
                    
                    {photo.status === 'uploading' && (
                      <p className="text-sm text-status-info font-body mt-1 flex items-center gap-2">
                        <Loader2 className="icon-sm animate-spin" />
                        Uploading...
                      </p>
                    )}
                    
                    {photo.status === 'done' && (
                      <p className="text-sm text-status-success font-body mt-1">
                        ✓ Uploaded successfully
                      </p>
                    )}
                    
                    {photo.status === 'error' && (
                      <p className="text-sm text-status-error font-body mt-1">
                        ✗ {photo.error}
                      </p>
                    )}
                  </div>
                  
                  {/* Remove button */}
                  {photo.status !== 'uploading' && photo.status !== 'done' && (
                    <button
                      onClick={() => handleRemovePhoto(photo.filename)}
                      className="p-1.5 hover:bg-slate-700/50 rounded transition-default flex-shrink-0"
                    >
                      <X className="w-4 h-4 text-glass-muted" />
                    </button>
                  )}
                </div>
                
                {/* Conflict: Brand selection buttons */}
                {photo.status === 'conflict' && (
                  <div className="mt-3 pt-3 border-t border-slate-700">
                    <p className="text-xs text-status-warning font-body mb-2">
                      Found {photo.matches.length} items with this name. Select brand:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {photo.matches.map(match => (
                        <button
                          key={match.id}
                          onClick={() => handleSelectMatch(photo.filename, match)}
                          className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-glass rounded-lg font-body transition-default active:bg-slate-500"
                        >
                          {match.brand}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* No match: Manual search */}
                {photo.status === 'no-match' && (
                  <div className="mt-3 pt-3 border-t border-slate-700">
                    <button
                      onClick={() => setExpandedPhoto(expandedPhoto === photo.filename ? null : photo.filename)}
                      className="text-sm text-status-accent hover:text-purple-300 font-body flex items-center gap-1 w-full justify-between"
                    >
                      <span>Search manually to find a match</span>
                      {expandedPhoto === photo.filename ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {expandedPhoto === photo.filename && (
                      <div className="mt-3 p-3 bg-slate-900/50 rounded-lg">
                        <input
                          type="text"
                          placeholder="Search items..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-base sm:text-sm text-glass font-body mb-2"
                        />
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {filteredItems.map(item => (
                            <button
                              key={item.id}
                              onClick={() => handleManualMatch(photo.filename, item)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 active:bg-slate-600 rounded-lg text-glass-secondary hover:text-glass font-body transition-default"
                            >
                              {item.brand} - {item.name}
                            </button>
                          ))}
                          {filteredItems.length === 0 && (
                            <p className="text-sm text-glass-muted text-center py-4">No items found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Storage limit warning */}
        {photosSkippedStorage > 0 && (
          <div className="px-4 pb-2">
            <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <HardDrive className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-300 font-body text-sm">
                  {photosSkippedStorage} photo{photosSkippedStorage !== 1 ? 's' : ''} skipped — storage full
                </p>
                <p className="text-xs text-amber-200/80 font-body">
                  Upgrade your plan to upload the remaining photos.
                </p>
              </div>
              {nextTier && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-xs font-medium hover:from-purple-600 hover:to-pink-600 transition-default flex-shrink-0"
                >
                  Upgrade
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-slate-600 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="text-sm text-glass-muted font-body text-center sm:text-left order-1 sm:order-1">
            {isUploading 
              ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...`
              : stats.readyToUpload > 0 
                ? `${stats.readyToUpload} photos ready to upload`
                : 'Match photos to items, then upload'
            }
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 order-2 sm:order-2">
            <button
              onClick={onClose}
              className="px-4 py-2.5 sm:py-2 text-glass-secondary hover:text-glass font-medium font-body order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              onClick={handleUploadAll}
              disabled={isUploading || stats.readyToUpload === 0}
              className="px-6 py-3 sm:py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-default disabled:opacity-50 disabled:cursor-not-allowed font-body flex items-center justify-center gap-2 order-1 sm:order-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="icon-sm animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="icon-sm" />
                  Upload {stats.readyToUpload} Photos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Storage Upgrade Modal */}
      {showUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          highlightTier={nextTier?.id}
        />
      )}
    </div>
  );
}
